import type { ReactNode } from "react";
import { IonBackButton, IonButtons, IonHeader, IonToolbar } from "@ionic/react";
import { cn } from "../../lib/utils";

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** When set, shows a back button that falls back to this route. */
  backHref?: string;
  /** Right-aligned actions (IconButtons, Buttons…). */
  actions?: ReactNode;
  /**
   * "large" = top-level tab page with a big left-aligned title.
   * "compact" = sub page with a centered-ish title next to the back button.
   */
  variant?: "large" | "compact";
  className?: string;
}

/**
 * Consistent page header for every screen. Wraps Ionic's header/toolbar so
 * safe-area insets and back navigation keep working.
 */
export function PageHeader({
  title,
  subtitle,
  backHref,
  actions,
  variant = backHref ? "compact" : "large",
  className,
}: PageHeaderProps) {
  return (
    <IonHeader className={cn("ion-no-border", className)}>
      <IonToolbar>
        {backHref && (
          <IonButtons slot="start">
            <IonBackButton defaultHref={backHref} text="" aria-label="Back" />
          </IonButtons>
        )}
        <div
          className={cn(
            "max-w-lg mx-auto w-full flex items-center gap-3",
            backHref ? "px-1 py-2" : "px-4 pt-3 pb-2"
          )}
        >
          <div className="flex-1 min-w-0">
            <h1
              className={cn(
                "font-extrabold tracking-tight text-foreground truncate",
                variant === "large" ? "text-[28px] leading-tight" : "text-lg"
              )}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
          {actions && <div className="flex items-center gap-1 shrink-0">{actions}</div>}
        </div>
      </IonToolbar>
    </IonHeader>
  );
}

export default PageHeader;
